import { t as __t } from "i18next";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DatePicker from "react-datepicker";
import moment from "moment";
import Swal from "sweetalert2";

import CopyrightFooter from "../../../components/footer/employers/CopyrightFooter";
import DashboardHeader from "../../../components/header/DashboardHeader";
import DashboardEmployerSidebar from "../../../components/header/DashboardSidebar";
import MobileMenu from "../../../components/header/MobileMenu";
import SmartTable from "../../../components/common/table-component";

import "../../../components/i18n/lang";

import reports from "../../../data/reportAgent";
import paymentStatus from "../../../data/paymentStatus";

import {
	callExportSummaryExcel,
	getExportVehicleSummary,
} from "../../../api/export";

import {
	encodeBase64,
	FormatCurrencys,
} from "../../../utils/commonUtil";

const Index = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { t } = useTranslation();
	const location = useLocation();

	const now = new Date();

	const startOfMonth = new Date(
		now.getFullYear(),
		now.getMonth(),
		1,
		0,
		0,
		0,
		0
	);

	const [request, setRequest] = useState({
		status: "",
		page: 1,
		maxResult: 10,
		keyword: "",
		reportType: 1,
		startYear: startOfMonth,
		endYear: now,
	});

	const [dataList, setDataList] = useState([]);
	const [loading, setLoading] = useState(false);

	/**
	 * ແປງ Date ຫຼື string ເປັນ YYYY-MM-DD
	 */
	const formatRequestDate = (date) => {
		if (!date) return "";

		const parsedDate = moment(date);

		if (!parsedDate.isValid()) {
			return "";
		}

		return parsedDate.format("YYYY-MM-DD");
	};

	/**
	 * ສ້າງ request ສຳລັບ API
	 */
	const buildRequestData = useCallback(
		(extraParams = {}) => {
			return {
				...request,
				...extraParams,
				reportType: Number(request.reportType),
				startYear: formatRequestDate(request.startYear),
				endYear: formatRequestDate(request.endYear),
			};
		},
		[request]
	);

	/**
	 * ໂຫຼດຂໍ້ມູນລາຍງານ
	 */
	const loadData = useCallback(
		async (extraParams = {}) => {
			try {
				setLoading(true);

				const data = buildRequestData(extraParams);

				console.log("Vehicle summary request:", data);

				const rsp = await dispatch(
					getExportVehicleSummary(data)
				);

				if (rsp?.payload?.responseCode === "00000") {
					setDataList(rsp?.payload?.data || []);
				} else {
					setDataList([]);
				}
			} catch (error) {
				console.error("Load vehicle summary error:", error);

				Swal.fire(
					__t("uiText.text023"),
					__t("uiText.text096"),
					"error"
				);
			} finally {
				setLoading(false);
			}
		},
		[buildRequestData, dispatch]
	);

	/**
	 * ໄປໜ້າລາຍລະອຽດ
	 *
	 * ຈຸດສຳຄັນ:
	 * ຄ່າ status ຈະຖືກດຶງຈາກ request ປັດຈຸບັນ
	 */
	const openVehicleInsuranceDetail = useCallback(
		(item) => {
			const params = {
				agentCode: item?.agentCode || "",
				reportValue: item?.region || "",
				status: request.status,
				fromDate: formatRequestDate(request.startYear),
				toDate: formatRequestDate(request.endYear),
			};

			console.log("Navigate detail params:", params);

			const encodedParams = encodeBase64(
				JSON.stringify(params)
			);

			navigate(
				`/admin/report/vehicleReportSummary/detail/${encodedParams}`
			);
		},
		[
			navigate,
			request.status,
			request.startYear,
			request.endYear,
		]
	);

	/**
	 * Column ທີ່ໃຊ້ຮ່ວມກັນ
	 */
	const amountColumns = useMemo(
		() => [
			{
				label: "summarySumNetPremium",
				key: "sumNetPremium",
				style: { minWidth: "200px" },
				render: (val) =>
					val !== null && val !== undefined
						? FormatCurrencys(val)
						: 0,
			},
			{
				label: "summarySumVat07",
				key: "sumVat_07",
				style: { minWidth: "200px" },
				render: (val) =>
					val !== null && val !== undefined
						? FormatCurrencys(val)
						: 0,
			},
			{
				label: "summarySumRegisterFee",
				key: "sumRegisterFee",
				style: { minWidth: "200px" },
				render: (val) =>
					val !== null && val !== undefined
						? FormatCurrencys(val)
						: 0,
			},
			{
				label: "summarySumTotalPremium",
				key: "sumTotalPremium",
				style: { minWidth: "200px" },
				render: (val) =>
					val !== null && val !== undefined
						? FormatCurrencys(val)
						: 0,
			},
			{
				label: "summarySumCommission",
				key: "sumCommission",
				style: { minWidth: "200px" },
				render: (val) =>
					val !== null && val !== undefined
						? FormatCurrencys(val)
						: 0,
			},
			{
				label: "summarySumPaidToLti",
				key: "sumPaidToLti",
				style: { minWidth: "200px" },
				render: (val) =>
					val !== null && val !== undefined
						? FormatCurrencys(val)
						: 0,
			},
		],
		[]
	);

	/**
	 * ກຳນົດ columns ຕາມ reportType
	 *
	 * ໃຊ້ useMemo ເພື່ອໃຫ້ columns ອັບເດດ
	 * ເມື່ອ request.status ຫຼືວັນທີປ່ຽນ
	 */
	const columns = useMemo(() => {
		const normalPolicyCountColumn = {
			label: "summaryPolicyCount",
			key: "policyCount",
			style: { minWidth: "200px" },
			render: (val) => val ?? 0,
		};

		const policyCountLinkColumn = {
			label: "summaryPolicyCount",
			key: "policyCount",
			style: { minWidth: "200px" },
			render: (val, item) => (
				<button
					type="button"
					className="btn btn-link p-0"
					onClick={() =>
						openVehicleInsuranceDetail(item)
					}
				>
					{val ?? 0}
				</button>
			),
		};

		switch (Number(request.reportType)) {
			case 1:
				return [
					{
						label: "summaryMonthName",
						key: "monthName",
						style: { minWidth: "200px" },
						render: (val) => val || "-",
					},
					policyCountLinkColumn,
					...amountColumns,
				];

			case 2:
				return [
					{
						label: "summaryRegion",
						key: "region",
						style: { minWidth: "200px" },
						render: (val) => val || "-",
					},
					policyCountLinkColumn,
					...amountColumns,
				];

			case 3:
				return [
					{
						label: "summaryAgentCode",
						key: "agentCode",
						style: { minWidth: "200px" },
						render: (val) => val || "-",
					},
					policyCountLinkColumn,
					...amountColumns,
				];

			case 4:
				return [
					{
						label: "summaryAgentCode",
						key: "agentCode",
						style: { minWidth: "200px" },
						render: (val) => val || "-",
					},
					{
						label: "summaryAgentName",
						key: "fullName",
						style: { minWidth: "200px" },
						render: (val) => val || "-",
					},
					policyCountLinkColumn,
					...amountColumns,
				];

			default:
				return [];
		}
	}, [
		request.reportType,
		request.status,
		request.startYear,
		request.endYear,
		amountColumns,
		openVehicleInsuranceDetail,
	]);

	/**
	 * ໂຫຼດຂໍ້ມູນເມື່ອ reportType ປ່ຽນ
	 */
	useEffect(() => {
		loadData({
			page: 1,
		});
	}, [request.reportType]);

	/**
	 * ປ່ຽນ report type
	 */
	const selectReportType = (e) => {
		const value = Number(e.target.value);

		setRequest((prev) => ({
			...prev,
			reportType: value,
			page: 1,
		}));
	};

	/**
	 * ປ່ຽນ status
	 */
	const selectStatus = (e) => {
		const value = e.target.value;

		setRequest((prev) => ({
			...prev,
			status: value,
			page: 1,
		}));
	};

	/**
	 * ຄົ້ນຫາ
	 */
	const onSearch = async (e) => {
		e.preventDefault();

		await loadData({
			page: 1,
		});
	};

	/**
	 * Export Excel
	 */
	const downloadExcel = async () => {
		try {
			const totalRecords =
				dataList?.totalRecords ||
				dataList?.pagination?.totalRecords ||
				dataList?.length ||
				request.maxResult;

			const data = buildRequestData({
				page: 1,
				maxResult: totalRecords,
			});

			console.log("Export Excel request:", data);

			const response = await callExportSummaryExcel(data);

			if (!response) {
				Swal.fire(
					__t("uiText.text023"),
					__t("uiText.text336"),
					"error"
				);

				return;
			}

			const url = URL.createObjectURL(response);
			const link = document.createElement("a");

			link.href = url;
			link.download = __t("uiText.text337", { value1: moment().format(
				"YYYYMMDDHHmmss"
			) });

			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);

			URL.revokeObjectURL(url);
		} catch (error) {
			console.error("Error downloading Excel:", error);

			Swal.fire(
				__t("uiText.text023"),
				__t("uiText.text336"),
				"error"
			);
		}
	};

	return (
		<div className="page-wrapper dashboard">
			<span className="header-span"></span>

			<DashboardHeader />
			<MobileMenu />
			<DashboardEmployerSidebar />

			<section className="user-dashboard">
				<div className="dashboard-outer">
					<div className="upper-title-box">
						<h4>
							{t(
								"menu.report.vehicle-report-summary"
							)}
						</h4>

						<div className="group-button-header">
							<button
								type="button"
								className="btn btn-success"
								onClick={downloadExcel}
								disabled={loading}
							>
								<i className="fas fa-file-export"></i>
								&nbsp;
								{t("button.exportExcel")}
							</button>
						</div>
					</div>

					<div className="row">
						<div className="col-12">
							<div className="ls-widget">
								<div className="tabs-box">
									<div className="widget-content p-4">
										<form
											className="default-form"
											onSubmit={onSearch}
										>
											<div className="row">
												<div className="col-md-2 form-group">
													<label>
														{t("startDate")}
													</label>

													<DatePicker
														selected={
															request.startYear
														}
														onChange={(date) =>
															setRequest(
																(prev) => ({
																	...prev,
																	startYear:
																		date,
																	page: 1,
																})
															)
														}
														selectsStart
														startDate={
															request.startYear
														}
														endDate={
															request.endYear
														}
														maxDate={
															request.endYear ||
															new Date()
														}
														dateFormat="dd/MM/yyyy"
														placeholderText={t(
															"startDate"
														)}
														className="form-control-uld"
													/>
												</div>

												<div className="col-md-2 form-group">
													<label>
														{t("endDate")}
													</label>

													<DatePicker
														selected={
															request.endYear
														}
														onChange={(date) =>
															setRequest(
																(prev) => ({
																	...prev,
																	endYear:
																		date,
																	page: 1,
																})
															)
														}
														selectsEnd
														startDate={
															request.startYear
														}
														endDate={
															request.endYear
														}
														minDate={
															request.startYear
														}
														maxDate={new Date()}
														dateFormat="dd/MM/yyyy"
														placeholderText={t(
															"endDate"
														)}
														className="form-control-uld"
													/>
												</div>

												<div className="col-md-3 form-group">
													<label>
														{t(
															"report.customer.reportTypeName"
														)}
													</label>

													<select
														className="chosen-single form-select"
														onChange={
															selectReportType
														}
														value={
															request.reportType
														}
													>
														{reports.reportTypeInsuranSummary.map(
															(item) => (
																<option
																	key={
																		item.id
																	}
																	value={
																		item.id
																	}
																>
																	{t(
																		item.name
																	)}
																</option>
															)
														)}
													</select>
												</div>

												<div className="col-md-2 form-group">
													<label>
														{t("status.text")}
													</label>

													<select
														className="chosen-single form-select"
														onChange={
															selectStatus
														}
														value={
															request.status
														}
													>
														<option value="">
															{t(
																"selectedAll"
															)}
														</option>

														{paymentStatus.map(
															(item) => (
																<option
																	key={
																		item.id
																	}
																	value={
																		item.status
																	}
																>
																	{t(
																		item.name
																	)}
																</option>
															)
														)}
													</select>
												</div>

												<div className="col-auto auto-button">
													<label
														style={{
															marginTop: 6,
														}}
													>
														&nbsp;
													</label>

													<button
														type="submit"
														className="theme-btn btn-style-one w-100"
														disabled={loading}
													>
														<i className="fas fa-search"></i>
														&nbsp;
														{loading
															? t(
																"loading"
															)
															: t(
																"button.search"
															)}
													</button>
												</div>
											</div>
										</form>
									</div>
								</div>
							</div>
						</div>
					</div>

					<div className="row">
						<div className="col-12">
							<div className="ls-widget">
								<div className="tabs-box">
									<div className="widget-content p-4">
										<SmartTable
											columns={columns}
											data={dataList}
											keyword={
												request.keyword
											}
											onLazyLoad={loadData}
											loading={loading}
										/>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</section>

			<CopyrightFooter />
		</div>
	);
};

export default Index;